import {InfiniteList, Layout} from "@components";
import React, {useEffect, useMemo, useRef, useState} from "react";
import styles from "./style.module.scss";
import {useRecoilValue} from "recoil";
import {states} from "@recoils";
import * as Datas from "@data";
import {api, constant} from "@utils";
import {InputBox, Pagination} from "@adminComponents";
import {Images, Svgs} from "@assets";
import {DetailModal} from "./DetailModal";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSearch} from "@fortawesome/free-solid-svg-icons";
import _ from "lodash";

export default function HomePage() {
  const advData = useRecoilValue(states.advDataState);
  const infoData = useRecoilValue(states.infoDataState);
  const [listData, setListData] =
    useState<Datas.ResultPagingResponse<Datas.IMemberResponse[]>>();
  const [maxSequnce, setMaxSequnce] = useState<number>();
  const [sequnce, setSequnce] = useState<number | undefined>(1);
  const [detail, setDetail] = useState<Datas.IMemberResponse>();
  const [searchTxt, setSearchTxt] = useState("");
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const sequnceRef = useRef<number | undefined>(sequnce);
  const searchTxtRef = useRef(searchTxt);
  const {isPc, isTablet} = useMemo(() => {
    return {
      isPc: window.innerWidth > constant.TABLET_WIDTH,
      isTablet:
        window.innerWidth <= constant.TABLET_WIDTH &&
        window.innerWidth > constant.MOBILE_WIDTH,
    };
  }, []);

  const advList = useMemo(() => {
    const fAdv = advData.find(
      item => (item.type || "").toLocaleUpperCase() === "F",
    );
    const gAdv = advData.find(
      item => (item.type || "").toLocaleUpperCase() === "G",
    );
    const hAdv = advData.find(
      item => (item.type || "").toLocaleUpperCase() === "H",
    );
    const iAdv = advData.find(
      item => (item.type || "").toLocaleUpperCase() === "I",
    );
    return [fAdv, gAdv, hAdv, iAdv].filter(item => !!item);
  }, [advData]);
  const {sequnceList} = useMemo(() => {
    const maxSeqStr = (infoData || []).find(
      item => item.type === "sequnce",
    )?.content;
    const maxSeq = isNaN(Number(maxSeqStr)) ? 1 : Number(maxSeqStr);
    setMaxSequnce(maxSeq);
    setSequnce(maxSeq);
    const sequnceList = Array.from({length: maxSeq}, (_, index) => index + 1);
    return {sequnceList};
  }, [infoData]);

  async function getList(page: number = 1) {
    try {
      if (!sequnceRef.current && !searchTxtRef.current) {
        return setListData(undefined);
      }
      if (loadingRef.current) {
        return;
      }
      if (page === 1) {
        setListData(undefined);
      }
      loadingRef.current = true;
      setLoading(true);
      const {success, data} = await api.v1.member.getList({
        page,
        limit: 24,
        isFront: true,
        sequnce: sequnceRef.current,
        searchTxt: searchTxtRef.current,
      });
      loadingRef.current = false;
      setLoading(false);
      if (success) {
        if (page === 1) {
          setListData(data);
        } else {
          setListData(prev => ({
            ...prev,
            result: [...(prev?.result || []), ...(data.result || [])],
            page: data.page,
          }));
        }
      }
    } catch (error) {
      console.error("getList error", error);
      loadingRef.current = false;
      setLoading(false);
    }
  }

  const debounceGetList = _.debounce(page => getList(page), 500);

  useEffect(() => {
    if (!!searchTxt) {
      sequnceRef.current = undefined;
      setSequnce(undefined);
    } else {
      sequnceRef.current = sequnce;
    }
    searchTxtRef.current = searchTxt;
    debounceGetList(1);
  }, [searchTxt]);

  useEffect(() => {
    sequnceRef.current = sequnce;
    debounceGetList(1);
  }, [sequnce]);

  return (
    <>
      <Layout>
        <div className={styles.container}>
          <div className={styles.advContainer}>
            {advList.map(item => (
              <div
                key={item.no}
                className={[
                  styles.advItem,
                  !!item.link ? styles.active : undefined,
                ].join(" ")}
                onClick={() => {
                  if (item.link) {
                    window.open(item.link, "_blank");
                  }
                }}>
                <img src={item.files?.[0]?.url} alt={item.title || ""} />
              </div>
            ))}
          </div>
          <div className={styles.searchWrapper}>
            <InputBox
              key={1}
              value={searchTxt}
              onValue={setSearchTxt}
              onEnter={() => getList(1)}
              boxClassName={styles.searchInput}
              placeholder="검색어를 입력해주세요"
              rightContent={
                <div className={styles.icon} onClick={() => getList(1)}>
                  <FontAwesomeIcon icon={faSearch} />
                </div>
              }
            />
          </div>
          <div className={styles.sequnceWrapper}>
            <div className={styles.sequnceContainer}>
              {sequnceList.map(seq => (
                <div
                  key={seq}
                  onClick={() => setSequnce(seq)}
                  className={[
                    styles.sequnceItem,
                    sequnce === seq ? styles.active : undefined,
                  ].join(" ")}>
                  {seq}기 회원현황
                </div>
              ))}
            </div>
          </div>
          <div className={styles.listContainer}>
            {listData?.page?.maxcnt === 0 || (!listData && !loading) ? (
              <div className={styles.empty}>조회된 회원이 없습니다.</div>
            ) : (
              <InfiniteList
                key={sequnce}
                list={listData?.result || []}
                page={listData?.page}
                getList={getList}
                isUsingLoading={true}
                containerClass={styles.list}
                loadingRef={loadingRef}
                renderItem={item => {
                  const img_url = item.photo?.[0]?.url;
                  return (
                    <div
                      key={item.no}
                      className={styles.item}
                      onClick={() => setDetail(item)}>
                      <div className={styles.infoContainer}>
                        <div className={styles.img}>
                          <img
                            src={img_url ?? Images.noImage}
                            alt={item.name}
                          />
                        </div>
                        <div className={styles.title}>
                          {item.sequnce}기 {item.position} <b>{item.name}</b>
                          {!!item.coach && <div>{item.coach}</div>}
                          {!!item.company && <div>{item.company}</div>}
                        </div>
                        <div className={styles.info}>
                          {!!item.company_address && (
                            <div className={styles.row}>
                              <div>
                                <Svgs.Location />
                              </div>
                              {item.company_address}
                            </div>
                          )}
                          {!!item.tel && (
                            <div className={styles.row}>
                              <div>
                                <Svgs.Phone />
                              </div>
                              {item.tel}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        disabled={!item.tel}
                        className={styles.btn}
                        onClick={() => window.open(`tel:${item.tel}`)}>
                        전화걸기
                      </button>
                    </div>
                  );
                }}
              />
            )}
          </div>
        </div>
      </Layout>
      <DetailModal detail={detail} close={() => setDetail(undefined)} />
    </>
  );
}
